import { ChatType } from '../types/monitor/ChatType';
import { MonitorType } from '../types/monitor/MonitorType';
import { ProjectStatsResponse } from '../services/monitor/keywords/stats/MonitorPromptsKeywordsStatsResponse';
import { canShowModel } from '../utils/utils';
import { getMonitorTypesForChatType, MODEL_KEY_TO_CHAT_TYPES } from './MonitorTypeHelpers';

// Readiness of a single model (canShowModel key, e.g. 'GPT') for a freshly-created project:
// `hasData` flips to true as soon as that model's querying produces its first stats row.
export interface ModelReadiness {
  key: string;
  hasData: boolean;
}

// AI_OVERVIEW and AI_MODE are a single account model (gated by the same feature), so canShowModel
// returns true for both keys whenever either is enabled. Collapse AI_MODE into AI_OVERVIEW — dropping
// the duplicate entry and unioning its chat types — so the loader counts them once and still detects
// readiness from data arriving under either. (AI_OVERVIEW_NEW is a separate model, left untouched.)
const getMergedEnabledModels = (
  features: string[]
): Array<{ key: string; chatTypes: ChatType[] }> => {
  const aiModeChatTypes = MODEL_KEY_TO_CHAT_TYPES.find((m) => m.key === 'AI_MODE')?.chatTypes ?? [];
  // Label the merged model by whichever feature the account actually has, so an AI_MODE account reads
  // "AI Mode" (not "AI Overview"). AI_OVERVIEW takes precedence when both are set.
  const aiKey = features.includes('AI_OVERVIEW') ? 'AI_OVERVIEW' : 'AI_MODE';
  return MODEL_KEY_TO_CHAT_TYPES.filter(
    ({ key }) => key !== 'AI_MODE' && canShowModel(key, features)
  ).map((model) =>
    model.key === 'AI_OVERVIEW'
      ? { key: aiKey, chatTypes: [...model.chatTypes, ...aiModeChatTypes] }
      : model
  );
};

// The account's enabled model keys, in a stable order. Available immediately from features so the
// loader can render a stable step list from the first frame (no pop-in as data lands).
export const getEnabledModelKeys = (features: string[]): string[] =>
  getMergedEnabledModels(features).map(({ key }) => key);

// Per-model readiness for the account's enabled models, derived the same way as `useModelsWithoutData`
// (a model "has data" once any stats entry with a non-null averagePosition maps to one of its
// MonitorTypes). Shared by the setup loader and the dashboard's live states so both agree.
export const getEnabledModelReadiness = (
  features: string[],
  stats: ProjectStatsResponse
): ModelReadiness[] => {
  const withData = new Set<MonitorType>();
  stats.monitorPromptsKeywordsStatsResponse.forEach((entry) => {
    if (entry.averagePosition !== null && entry.chatType) {
      getMonitorTypesForChatType(entry.chatType as ChatType).forEach((mt) => withData.add(mt));
    }
  });

  return getMergedEnabledModels(features).map(({ key, chatTypes }) => {
    const modelMonitorTypes = chatTypes.flatMap((ct) => getMonitorTypesForChatType(ct));
    return { key, hasData: modelMonitorTypes.some((mt) => withData.has(mt)) };
  });
};

// Snapshot the setup loader renders. Refreshed on each poll while the project populates.
export interface SetupProgressSnapshot {
  models: ModelReadiness[];
  promptsTotal: number;
  promptsReady: number; // prompts whose first data has landed (status left PENDING/REFRESHING)
}
